import { Injectable } from '@angular/core';
import Konva from 'konva';
import { Subject } from 'rxjs';
import { Block } from '../../planogram/models/block';
import { POSMaterial } from '../../planogram/models/POSMaterials/POSMaterial';
import { ProductGroup } from '../../planogram/models/product-group';
import { Point } from '../models/point';

type DragOutsideItem = {
	key: ProductGroup;
	canvasProduct: Konva.Group;
};

type PosDragOutsideItem = {
	key: POSMaterial;
	canvasItem: Konva.Group;
};

type BlockDragOutsideProductItem = {
	key: ProductGroup;
	canvasItem: Konva.Group;
};

type BlockDragOutsideItem = {
	key: Block;
	canvasItem: Konva.Node;
	products?: Array<BlockDragOutsideProductItem>;
};

type PreviewEntry = {
	element: HTMLDivElement;
	rect: { left: number; top: number; width: number; height: number };
	dropPoint: Point;
	positionPoint?: Point;
};

type BlockPreviewEntry = PreviewEntry & {
	blockChild?: HTMLDivElement;
	productElements: Map<ProductGroup, HTMLDivElement>;
};

@Injectable()
export class DragOutsideService {
	private previewElements: Map<ProductGroup, PreviewEntry> = new Map();
	private posPreviewElements: Map<POSMaterial, PreviewEntry> = new Map();
	private blockPreviewElements: Map<Block, BlockPreviewEntry> = new Map();
	public readonly drop$ = new Subject<HTMLElement>();
	public readonly posDrop$ = new Subject<HTMLElement>();
	public readonly blockDrop$ = new Subject<HTMLElement>();
	public readonly redrawCanvas$ = new Subject<void>();
	private static readonly DROP_TARGET_SELECTOR = '[data-canvas-drop-target]';

	static getDropTargetCanvases(): Array<HTMLElement> {
		return Array.from(document.querySelectorAll(DragOutsideService.DROP_TARGET_SELECTOR))
			.filter((el: Element): el is HTMLElement => el instanceof HTMLElement);
	}

	private static findScrollableParent(element: HTMLElement): HTMLElement | null {
		let parent: HTMLElement | null = element.parentElement;

		while (parent) {
			const { overflow, overflowX, overflowY } = getComputedStyle(parent);

			if ([overflow, overflowX, overflowY].some((value: string) => value === 'auto' || value === 'scroll')) {
				return parent;
			}

			parent = parent.parentElement;
		}

		return null;
	}

	private static intersectRects(a: DOMRect, b: DOMRect): DOMRect {
		const left: number = Math.max(a.left, b.left);
		const top: number = Math.max(a.top, b.top);
		const right: number = Math.min(a.right, b.right);
		const bottom: number = Math.min(a.bottom, b.bottom);

		return new DOMRect(left, top, Math.max(0, right - left), Math.max(0, bottom - top));
	}

	static getVisibleBounds(element: HTMLElement): DOMRect {
		const elementRect: DOMRect = element.getBoundingClientRect();
		const scrollParent: HTMLElement | null = DragOutsideService.findScrollableParent(element);

		if (!scrollParent) return elementRect;

		return DragOutsideService.intersectRects(elementRect, scrollParent.getBoundingClientRect());
	}

	update(options: {
		items: Array<DragOutsideItem>;
		stage: Konva.Stage;
		container: HTMLElement;
		overCanvasTargets?: Array<HTMLElement>;
	}): void {
		const { items, stage, container, overCanvasTargets = [] } = options;
		if (!stage || !container) return;

		const stageContent = stage.getContent();
		if (!stageContent) return;

		const stageRect = stageContent.getBoundingClientRect();
		const containerRect = DragOutsideService.getVisibleBounds(container);
		const activeOutsideKeys = new Set<ProductGroup>();

		const rects = overCanvasTargets
			.filter((el) => !!el)
			.map((el) => el.getBoundingClientRect())
			.filter((rect) => rect.width > 0 && rect.height > 0);

		items.forEach(({ key, canvasProduct }) => {
			if (!canvasProduct || !canvasProduct.getLayer()) return;
			const rect = canvasProduct.getClientRect({ relativeTo: stage });

			const left = stageRect.left + rect.x;
			const top = stageRect.top + rect.y;
			const right = left + rect.width;
			const bottom = top + rect.height;

			const isOutside = left < containerRect.left
				|| top < containerRect.top
				|| right > containerRect.right
				|| bottom > containerRect.bottom;
			const isOccluded = rects.some((occluder) =>
				left < occluder.right
				&& right > occluder.left
				&& top < occluder.bottom
				&& bottom > occluder.top
			);
			if (!isOutside && !isOccluded) return;

			activeOutsideKeys.add(key);
			const anchor = canvasProduct.getAbsoluteTransform().point({ x: 0, y: canvasProduct.height() });
			const dropPoint: Point = { x: stageRect.left + anchor.x, y: stageRect.top + anchor.y };

			let preview = this.previewElements.get(key);
			if (!preview) {
				const element = this.createPreviewElement(canvasProduct);
				preview = { element, rect: { left, top, width: rect.width, height: rect.height }, dropPoint };
				this.previewElements.set(key, preview);
			}

			preview.element.style.left = `${left}px`;
			preview.element.style.top = `${top}px`;
			preview.element.style.width = `${rect.width}px`;
			preview.element.style.height = `${rect.height}px`;
			preview.rect = { left, top, width: rect.width, height: rect.height };
			preview.dropPoint = dropPoint;
		});

		Array.from(this.previewElements.entries()).forEach(([key, entry]) => {
			if (!activeOutsideKeys.has(key)) {
				entry.element.remove();
				this.previewElements.delete(key);
			}
		});
	}

	clear(): void {
		if (!this.previewElements.size) return;
		this.previewElements.forEach((entry) => entry.element.remove());
		this.previewElements.clear();
	}

	updatePos(options: {
		items: Array<PosDragOutsideItem>;
		stage: Konva.Stage;
		container: HTMLElement;
		overCanvasTargets?: Array<HTMLElement>;
	}): void {
		const { items, stage, container, overCanvasTargets = [] } = options;
		if (!stage || !container) return;

		const stageContent = stage.getContent();
		if (!stageContent) return;

		const stageRect: DOMRect = stageContent.getBoundingClientRect();
		const containerRect: DOMRect = DragOutsideService.getVisibleBounds(container);
		const activeOutsideKeys: Set<POSMaterial> = new Set<POSMaterial>();

		const rects = overCanvasTargets
			.filter((el) => !!el)
			.map((el) => el.getBoundingClientRect())
			.filter((rect) => rect.width > 0 && rect.height > 0);

		items.forEach(({ key, canvasItem }) => {
			if (!canvasItem || !canvasItem.getLayer()) return;

			const rect = canvasItem.getClientRect({ relativeTo: stage });
			const left = stageRect.left + rect.x;
			const top = stageRect.top + rect.y;
			const right = left + rect.width;
			const bottom = top + rect.height;
			const isOutside = left < containerRect.left
				|| top < containerRect.top
				|| right > containerRect.right
				|| bottom > containerRect.bottom;
			const isOccluded = rects.some((occluder) =>
				left < occluder.right
				&& right > occluder.left
				&& top < occluder.bottom
				&& bottom > occluder.top
			);

			if (!isOutside && !isOccluded) return;

			activeOutsideKeys.add(key);
			const topLeft = canvasItem.getAbsoluteTransform().point({ x: 0, y: 0 });
			const positionPoint: Point = { x: stageRect.left + topLeft.x, y: stageRect.top + topLeft.y };
			const dropPoint: Point = { x: left + rect.width / 2, y: top + rect.height / 2 };

			let preview = this.posPreviewElements.get(key);

			if (!preview) {
				const element = this.createPreviewElement(canvasItem);

				preview = { element, rect: { left, top, width: rect.width, height: rect.height }, dropPoint, positionPoint };
				this.posPreviewElements.set(key, preview);
			}

			preview.element.style.left = `${left}px`;
			preview.element.style.top = `${top}px`;
			preview.element.style.width = `${rect.width}px`;
			preview.element.style.height = `${rect.height}px`;
			preview.rect = { left, top, width: rect.width, height: rect.height };
			preview.dropPoint = dropPoint;
			preview.positionPoint = positionPoint;
		});

		Array.from(this.posPreviewElements.entries()).forEach(([key, entry]) => {
			if (!activeOutsideKeys.has(key)) {
				entry.element.remove();
				this.posPreviewElements.delete(key);
			}
		});
	}

	clearPos(): void {
		if (!this.posPreviewElements.size) return;
		this.posPreviewElements.forEach((entry) => entry.element.remove());
		this.posPreviewElements.clear();
	}

	getPosDropPoint(material: POSMaterial, stage: Konva.Stage): Point {
		const preview = this.posPreviewElements.get(material);
		const positionPoint: Point = preview?.positionPoint ?? preview?.dropPoint;
		const stageRect: DOMRect = stage?.getContent()?.getBoundingClientRect();

		if (positionPoint && stageRect) {
			return {
				x: positionPoint.x - stageRect.left,
				y: positionPoint.y - stageRect.top
			};
		}

		return null;
	}

	findPosTargetCanvas(material: POSMaterial, canvases: Array<HTMLElement>, sourceCanvas: HTMLElement): HTMLElement {
		if (!this.posPreviewElements.size) return sourceCanvas;
        const preview = this.posPreviewElements.get(material);
		if (!preview) return sourceCanvas;
        const targetCanvas = this.findTargetCanvas(preview.dropPoint, canvases);
        if (!targetCanvas) return sourceCanvas;
        return targetCanvas;
	}

	updateBlock(options: {
		items: Array<BlockDragOutsideItem>;
		stage: Konva.Stage;
		container: HTMLElement;
		overCanvasTargets?: Array<HTMLElement>;
	}): void {
		const { items, stage, container, overCanvasTargets = [] } = options;
		if (!stage || !container) return;

		const stageContent = stage.getContent();
		if (!stageContent) return;

		const stageRect: DOMRect = stageContent.getBoundingClientRect();
		const containerRect: DOMRect = DragOutsideService.getVisibleBounds(container);
		const activeOutsideKeys: Set<Block> = new Set<Block>();

		const rects = overCanvasTargets
			.filter((el) => !!el)
			.map((el) => el.getBoundingClientRect())
			.filter((rect) => rect.width > 0 && rect.height > 0);

		items.forEach(({ key, canvasItem, products = [] }) => {
			if (!canvasItem || !canvasItem.getLayer()) return;

			const blockStageRect = canvasItem.getClientRect({ relativeTo: stage });
			const blockScreenLeft = stageRect.left + blockStageRect.x;
			const blockScreenTop = stageRect.top + blockStageRect.y;
			const isOutside = blockScreenLeft < containerRect.left
				|| blockScreenTop < containerRect.top
				|| blockScreenLeft + blockStageRect.width > containerRect.right
				|| blockScreenTop + blockStageRect.height > containerRect.bottom;
			const isOccluded = rects.some((occluder) =>
				blockScreenLeft < occluder.right
				&& blockScreenLeft + blockStageRect.width > occluder.left
				&& blockScreenTop < occluder.bottom
				&& blockScreenTop + blockStageRect.height > occluder.top
			);

			if (!isOutside && !isOccluded) return;

			activeOutsideKeys.add(key);

			this.upsertBlockPreview(
				key,
				stage,
				stageRect,
				canvasItem,
				blockStageRect,
				blockScreenLeft,
				blockScreenTop,
				products.filter(({ canvasItem: canvasProduct }) => canvasProduct?.getLayer())
			);
		});

		Array.from(this.blockPreviewElements.entries()).forEach(([key, entry]) => {
			if (!activeOutsideKeys.has(key)) {
				entry.element.remove();
				this.blockPreviewElements.delete(key);
			}
		});
	}

	clearBlock(): void {
		if (!this.blockPreviewElements.size) return;
		this.blockPreviewElements.forEach((entry: BlockPreviewEntry) => entry.element.remove());
		this.blockPreviewElements.clear();
	}

	hasBlockPreview(block: Block): boolean {
		return this.blockPreviewElements.has(block);
	}

	getBlockDropPoint(block: Block, stage: Konva.Stage): Point {
		const preview = this.blockPreviewElements.get(block);
		const stageRect: DOMRect = stage?.getContent()?.getBoundingClientRect();
		const positionPoint: Point = preview?.positionPoint
			?? (preview?.rect ? { x: preview.rect.left, y: preview.rect.top } : null);

		if (positionPoint && stageRect) {
			return {
				x: positionPoint.x - stageRect.left,
				y: positionPoint.y - stageRect.top
			};
		}

		return null;
	}

	findBlockTargetCanvas(block: Block, canvases: Array<HTMLElement>, sourceCanvas: HTMLElement): HTMLElement {
		if (!this.blockPreviewElements.size) return sourceCanvas;
		const preview = this.blockPreviewElements.get(block);
		if (!preview) return sourceCanvas;
		const targetCanvas = this.findTargetCanvas(preview.dropPoint, canvases);
		if (!targetCanvas) return sourceCanvas;
		return targetCanvas;
	}

	getDropPoint(pg: ProductGroup, stage: Konva.Stage): Point {
		const dropPoint: Point = this.previewElements.get(pg)?.dropPoint;
		const stageRect: DOMRect = stage?.getContent()?.getBoundingClientRect();

		if (dropPoint && stageRect) {
			return {
				x: dropPoint.x - stageRect.left,
				y: dropPoint.y - stageRect.top
			}
		} else {
			return null;
		}
	}

	emitDropIfTargetDifferent(activeProducts: ProductGroup[], canvases: HTMLElement[], sourceCanvas: HTMLElement): HTMLElement | null {
		if (!this.previewElements.size) return null;
		if (!activeProducts.length) return null;

		for (const product of activeProducts) {
			const preview = this.previewElements.get(product);
			if (!preview) continue;
			const targetCanvas = this.findTargetCanvas(preview.dropPoint, canvases);
			if (!targetCanvas || targetCanvas === sourceCanvas) continue;

			this.drop$.next(targetCanvas);
			this.clear();
			return targetCanvas;
		}

		return null;
	}

	private findTargetCanvas(point: Point, canvases: HTMLElement[]): HTMLElement | null {
		if (!canvases.length) return null;

		const elementAtPoint: HTMLElement | null = document.elementFromPoint(point.x, point.y) as HTMLElement | null;

		if (elementAtPoint) {
			let current: HTMLElement | null = elementAtPoint;

			while (current) {
				const match: HTMLElement | undefined = canvases.find((canvas: HTMLElement) => canvas === current);

				if (match) return match;

				current = current.parentElement;
			}
		}

		return canvases.find((canvas: HTMLElement) => {
			const rect: DOMRect = DragOutsideService.getVisibleBounds(canvas);

			if (rect.width <= 0 || rect.height <= 0) return false;

			return point.x >= rect.left
				&& point.x <= rect.right
				&& point.y >= rect.top
				&& point.y <= rect.bottom;
		}) || null;
	}

	private upsertBlockPreview(
		block: Block,
		stage: Konva.Stage,
		stageRect: DOMRect,
		blockCanvasItem: Konva.Node,
		blockStageRect: { x: number; y: number; width: number; height: number },
		blockScreenLeft: number,
		blockScreenTop: number,
		products: Array<BlockDragOutsideProductItem>
	): void {
		const productStageRects = products.map(({ canvasItem: canvasProduct }) =>
			canvasProduct.getClientRect({ relativeTo: stage })
		);
		const minStageX = productStageRects.length
			? Math.min(blockStageRect.x, ...productStageRects.map((rect) => rect.x))
			: blockStageRect.x;
		const minStageY = productStageRects.length
			? Math.min(blockStageRect.y, ...productStageRects.map((rect) => rect.y))
			: blockStageRect.y;
		const maxStageX = productStageRects.length
			? Math.max(blockStageRect.x + blockStageRect.width, ...productStageRects.map((rect) => rect.x + rect.width))
			: blockStageRect.x + blockStageRect.width;
		const maxStageY = productStageRects.length
			? Math.max(blockStageRect.y + blockStageRect.height, ...productStageRects.map((rect) => rect.y + rect.height))
			: blockStageRect.y + blockStageRect.height;
		const width = maxStageX - minStageX;
		const height = maxStageY - minStageY;
		const left = stageRect.left + minStageX;
		const top = stageRect.top + minStageY;
		const dropPoint: Point = { x: left + width / 2, y: top + height / 2 };
		let preview = this.blockPreviewElements.get(block);

		if (!preview) {
			const element = this.createPreviewWrapper();

			preview = {
				element,
				rect: { left, top, width, height },
				dropPoint,
				positionPoint: { x: blockScreenLeft, y: blockScreenTop },
				productElements: new Map()
			};

			products.forEach(({ key: product, canvasItem: canvasProduct }, index: number) => {
				const productRect = productStageRects[index];

				preview.productElements.set(
					product,
					this.createPreviewChild(
						element,
						canvasProduct,
						productRect.x - minStageX,
						productRect.y - minStageY,
						productRect.width,
						productRect.height
					)
				);
			});

			preview.blockChild = this.createPreviewChild(
				element,
				blockCanvasItem,
				blockStageRect.x - minStageX,
				blockStageRect.y - minStageY,
				blockStageRect.width,
				blockStageRect.height
			);
			this.blockPreviewElements.set(block, preview);
		}

		preview.element.style.left = `${left}px`;
		preview.element.style.top = `${top}px`;
		preview.element.style.width = `${width}px`;
		preview.element.style.height = `${height}px`;
		preview.rect = { left, top, width, height };
		preview.dropPoint = dropPoint;
		preview.positionPoint = { x: blockScreenLeft, y: blockScreenTop };

		if (preview.blockChild) {
			preview.blockChild.style.left = `${blockStageRect.x - minStageX}px`;
			preview.blockChild.style.top = `${blockStageRect.y - minStageY}px`;
			preview.blockChild.style.width = `${blockStageRect.width}px`;
			preview.blockChild.style.height = `${blockStageRect.height}px`;
		}

		products.forEach(({ key: product }, index: number) => {
			const productChild = preview.productElements.get(product);
			const productRect = productStageRects[index];

			if (!productChild || !productRect) return;

			productChild.style.left = `${productRect.x - minStageX}px`;
			productChild.style.top = `${productRect.y - minStageY}px`;
			productChild.style.width = `${productRect.width}px`;
			productChild.style.height = `${productRect.height}px`;
		});
	}

	private createPreviewWrapper(): HTMLDivElement {
		const element = document.createElement('div');
		element.className = 'drag-outside-preview-body';
		element.style.position = 'fixed';
		element.style.zIndex = '2147483647';
		element.style.pointerEvents = 'none';
		element.style.display = 'block';
		element.style.overflow = 'visible';
		document.body.appendChild(element);
		return element;
	}

	private createPreviewChild(
		wrapper: HTMLDivElement,
		canvasNode: Konva.Node,
		left: number,
		top: number,
		width: number,
		height: number
	): HTMLDivElement {
		const child = document.createElement('div');
		child.style.position = 'absolute';
		child.style.left = `${left}px`;
		child.style.top = `${top}px`;
		child.style.width = `${width}px`;
		child.style.height = `${height}px`;
		child.style.pointerEvents = 'none';

		const snapshotCanvas = canvasNode.toCanvas({ pixelRatio: 1 });
		snapshotCanvas.style.width = '100%';
		snapshotCanvas.style.height = '100%';
		snapshotCanvas.style.display = 'block';
		child.appendChild(snapshotCanvas);
		wrapper.appendChild(child);
		return child;
	}

	private createPreviewElement(canvasNode: Konva.Node): HTMLDivElement {
		const element = document.createElement('div');
		element.className = 'drag-outside-preview-body';
		element.style.position = 'fixed';
		element.style.zIndex = '2147483647';
		element.style.pointerEvents = 'none';
		element.style.display = 'block';
		element.style.transformOrigin = 'center';
		element.style.boxSizing = 'content-box';
		element.style.overflow = 'visible';

		const snapshotCanvas = canvasNode.toCanvas({ pixelRatio: 1 });
		snapshotCanvas.style.width = '100%';
		snapshotCanvas.style.height = '100%';
		snapshotCanvas.style.display = 'block';

		element.appendChild(snapshotCanvas);
		document.body.appendChild(element);
		return element;
	}
}
